// File: PreviewSection.js
import React from 'react';
import {
    Typography,
    Box,
    Divider,
    Chip,
    Grid,
    Paper
} from '@mui/material';

const PreviewSection = ({ resumeData }) => {
    return (
        <Grid
            item
            xs={12}
            md={6}
            sx={{
                height: '100%'
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    height: '100%',
                    mx: 'auto',
                    bgcolor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    '@media print': {
                        width: '100%',
                        height: 'auto',
                        boxShadow: 'none',
                        overflow: 'visible'
                    }
                }}
            >
                <Box
                    id="resume-preview"
                    sx={{
                        p: 2.5,
                        flexGrow: 1,
                        overflow: 'auto',
                        width: '21cm',
                        maxWidth: '100%',
                        mx: 'auto',
                        '@media print': {
                            p: 0,
                            overflow: 'visible'
                        }
                    }}
                >
                    {/* Header */}
                    <Box textAlign="center" mb={1} mt={-2}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {resumeData.personalInfo.name || 'Your Name'}
                        </Typography>

                        <Box mt={-1} display="flex" justifyContent="center" flexWrap="wrap" gap={0.5}>
                            {resumeData.personalInfo.title && (
                                <Typography variant="body2">
                                    {resumeData.personalInfo.title} |
                                </Typography>)}
                            {resumeData.personalInfo.email && (
                                <Typography variant="body2">
                                    {resumeData.personalInfo.email} |
                                </Typography>
                            )}
                            {resumeData.personalInfo.phone && (
                                <Typography variant="body2">
                                    {resumeData.personalInfo.phone} |
                                </Typography>
                            )}
                            {resumeData.personalInfo.location && (
                                <Typography variant="body2">
                                    {resumeData.personalInfo.location}
                                </Typography>
                            )}
                            {resumeData.personalInfo.linkedIn && (
                                <Typography variant="body2">
                                    {resumeData.personalInfo.linkedIn} |
                                </Typography>
                            )}
                            {resumeData.personalInfo.website && (
                                <Typography variant="body2">
                                    {resumeData.personalInfo.website}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Summary */}
                    {resumeData.summary && (
                        <>
                            <Box mb={1}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    PROFESSIONAL SUMMARY
                                </Typography>
                                <Divider sx={{ mb: -1 }} />

                                <Typography variant="body1">
                                    {resumeData.summary}
                                </Typography>
                            </Box>
                        </>
                    )}

                    {/* Experience */}
                    {resumeData.experience.some(exp => exp.company || exp.position) && (
                        <Box mt={-1}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                WORK EXPERIENCE
                            </Typography>
                            <Divider sx={{ mb: 1 }} />

                            {resumeData.experience.map((exp, index) => (
                                <Box key={index} mt={-1}>
                                    {(exp.company || exp.position) && (
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={-0.5}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {exp.position || 'Position'}
                                            </Typography>
                                            <Typography variant="body2">
                                                {exp.startDate || 'Start Date'} - {exp.endDate || 'Present'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {exp.company && (
                                        <Typography variant="subtitle2" gutterBottom>
                                            {exp.company}
                                        </Typography>
                                    )}

                                    {exp.achievements.some(achievement => achievement) && (
                                        <Box ml={2} mb={-2} mt={-0.5}>
                                            <ul style={{ marginTop: 2, paddingLeft: 2 }}>
                                                {exp.achievements.map((achievement, achievementIndex) => (
                                                    achievement && (
                                                        <li key={achievementIndex} style={{ paddingLeft: 4 }}>
                                                            <Typography variant="body2" sx={{ marginLeft: 1 }}>
                                                                {achievement}
                                                            </Typography>
                                                        </li>
                                                    )
                                                ))}
                                            </ul>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Education */}
                    {resumeData.education.some(edu => edu.institution || edu.degree) && (
                        <Box >
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                EDUCATION
                            </Typography>
                            <Divider />

                            {resumeData.education.map((edu, index) => (
                                <Box key={index} >
                                    {(edu.degree || edu.field) && (
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {edu.degree || 'Degree'}{edu.field ? ` in ${edu.field}` : ''}
                                            </Typography>
                                            <Typography variant="body2">
                                                {edu.graduationDate || 'Graduation Date'}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="subtitle2" gutterBottom>
                                            {edu.institution || 'Institution'}
                                        </Typography>
                                        {edu.gpa && (
                                            <Typography variant="body2">
                                                GPA: {edu.gpa}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Skills */}
                    {resumeData.skills.some(skill => skill) && (
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                SKILLS
                            </Typography>
                            <Divider />
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                {resumeData.skills.map((skill, index) => (
                                    skill && (
                                        <Chip
                                            key={index}
                                            label={skill}
                                            size="small"
                                            sx={{ m: 0.5 }}
                                        />
                                    )
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Projects */}
                    {resumeData.projects.some(proj => proj.title || proj.highlights.some(h => h)) && (
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                PROJECTS
                            </Typography>
                            <Divider />
                            {resumeData.projects.map((proj, index) => (
                                <Box key={index} >
                                    <Typography variant="body2">
                                        <strong>{proj.title || 'Project'}: </strong>
                                        {proj.highlights.filter(h => h).join(', ')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Paper>
        </Grid>
    );
};

export default PreviewSection;